import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTheme } from 'styled-components';

// Hooks
import { useClosingRoadStretches } from '../../../../hooks/useClosingRoadStretches';
import { useGpsToPK } from '../../../../hooks/useGpsToPK';
import { useSupportRoadCandidates } from '../../../../hooks/useSupportRoadCandidates';
import {
  useRoadNamesById,
  useRoadPkRange,
  useRoadRangesById,
} from '../../../../hooks/useRoadDirectionLabels';
import { roadPkRangeAFormulario } from '../../../../utils/pkValidation';
import {
  pointsWithin,
  pkWithinRange as pkWithin,
  resolveClosingStretches,
  pkWithinRange,
  type RoadStretch,
  type TrackPoint,
} from '../../../../utils/roadStretches';
import { getCachedRoadMeshEntries } from '../../../../hooks/usePrecacheRouteMesh';
import { getLocalTrack } from '../../../../offline/gpsTrackStore';
import { pkFromMeshes } from '../../../../utils/pkFromMesh';

// Components
import { Loader } from '../../../inputs/Loader';
import InputText from '../../../inputs/InputText';
import SearchableDropdown from '../../../inputs/SearchableDropdown';
import { InputTextArea } from '../../../inputs/InputTextArea';
import DropDownInput from '../../../inputs/DropDown/DropDownInput';
import { CheckBox } from '../../../inputs/CheckBox';
// La fila «casilla + texto» del modal de informes del recorrido, tal cual.
import { OptionRow } from '../DownloadHistoricModal/SurveillanceReportOptions/styles';

// Icons
import { AlertIcon } from '../../../../assets/icons/AlertIcon';

// Data
import { getLaneOptions } from '../../../../utils/catalogLabels';

// Types
import { ModalProps, PauseTimerFormData } from './types';

// Styles — composición centrada espejo de ChangeStateModal / ChangePauseCause.
import {
  ContainerForm,
  ContainerText,
  Title,
  Text,
  Section,
  PkFieldWrap,
  TextAreaWrap,
  ButtonRow,
  ActionButton,
  ModalContainerStyled,
  ContainerLoader,
} from './styles';

// Schema
import { pauseTimerSchema } from './schema';
import { registerPk } from '../../../../utils/pkField';
import { walkBackTrack, type WalkBackOutcome } from '../../../../utils/walkBackTrack';

/**
 * Rastro del prellenado del PK, SOLO en desarrollo.
 *
 * El campo se queda vacío sin dar un error: no hay excepción, no hay petición
 * fallida, no hay nada que mirar. Sin esto, diagnosticarlo es adivinar.
 * `import.meta.env.DEV` lo deja fuera del bundle de producción.
 */
const trace = (step: string, data?: unknown) => {
  if (import.meta.env.DEV) {
    console.log(`[pk-prefill] ${step}`, data ?? '');
  }
};

const parsePkString = (pk: string): { km: number; meter: number } | null => {
  const cleaned = pk.replace(/\s+/g, '');
  const match = cleaned.match(/^(\d+)\+(\d{1,4})$/);
  if (!match) return null;
  return { km: Number(match[1]), meter: Number(match[2]) };
};

// ═══════════════════════════════════════════════════════════════════
// COMPONENT — PauseTimerModal (formerly PauseSurveillanceRouteModal)
// ═══════════════════════════════════════════════════════════════════

/**
 * PauseTimerModal — Timer iteration 2026-04-15.
 *
 * Closes the active timer (RouteSegment) with a pause cause. The Tablet
 * supplies the closing PK and the reason; on confirm the parent finishes the
 * segment (the surveillance route auto-transitions to "paused").
 *
 * The directory is kept as `PauseSurveillanceRouteModal` for git history; the
 * exported component is renamed semantically to PauseTimerModal.
 */
export const PauseTimerModal = ({
  onClickClose,
  data,
}: ModalProps): JSX.Element => {
  const { t } = useTranslation(['pages', 'common', 'forms']);
  const theme = useTheme();
  const isLoading = data?.isLoading ?? false;
  // Cargando 2026-07-20: la mutation tarda 1,5-4s contra Atlas — sin esto el
  // modal quedaba con botones vivos (doble-submit posible) y sin feedback.
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Prevención P2: «Último tramo del turno». Fuera del formulario validado: no
  // es un dato del tramo, es qué hacer DESPUÉS de cerrarlo.
  const [isLastSegment, setIsLastSegment] = useState(false);

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    getFieldState,
    watch,
  } = useForm<PauseTimerFormData>({
    resolver: yupResolver(pauseTimerSchema),
    defaultValues: {
      pkEnd: '',
      pauseCause: '',
      pauseCauseDetail: '',
      lane: data?.defaultLane ?? '',
    },
  });

  // V4 D13: prefill GPS→PK on mount (cierre de tram) — el campo nace vacío
  // (ningún caller pasa `defaultPkEnd`, Task B3 2026-07-23) y este effect lo
  // rellena en cuanto el GPS resuelve; si el GPS falla/deniega, el operario
  // lo escribe a mano.
  const roadPkRange = useRoadPkRange(data?.roadId);

  // ═══════════════════════════════════════════════════════════════════
  // Por qué carreteras pasó el tramo
  // ═══════════════════════════════════════════════════════════════════
  //
  // El operario no cierra el tramo por pasarse de la troncal al ramal —solo
  // cierra cuando pasa algo—, así que llega aquí con un PK que puede no existir
  // en la vía donde lo abrió. El tramo NO se parte: terminar un tramo porque la
  // carretera cambió de nombre fabricaría un final sin causa, que es un evento
  // que no ocurrió. Se queda entero y registra sus trozos.
  //
  // Primero se miran los puntos ya grabados. Si no los hay —GPS apagado durante
  // la marcha— se cae a la bifurcación geométrica de las mallas: como anotar no
  // necesita ninguna HORA, con la frontera de PK basta, y esa frontera sale de
  // datos cacheados que funcionan sin cobertura.
  // ── Vía de APOYO (2026-09-23) ─────────────────────────────────────────
  //
  // La patrulla se fue a ayudar al otro tramo con este abierto y lo cierra
  // allí, en una vía de su concesión que el recorrido no declara. El GPS la
  // propone solo si ninguna del recorrido explica el punto, y entonces entra en
  // el cálculo de trozos: si el rastro explica cómo se llegó, el tramo acaba en
  // ella y el servidor la añade al recorrido. Sin rastro que lo explique no se
  // inventa nada: la validación sigue contra las vías del recorrido, como
  // antes, y el error sale aquí en vez de perderse en la cola offline.
  const supportCandidates = useSupportRoadCandidates(
    data?.roadIds,
    data?.concessionIds
  );
  const supportCandidatesRef = useRef(supportCandidates);
  supportCandidatesRef.current = supportCandidates;
  const [supportRoadId, setSupportRoadId] = useState<string | null>(null);
  const closingRoadIds = useMemo(
    () =>
      supportRoadId
        ? [...(data?.roadIds ?? []), supportRoadId]
        : (data?.roadIds ?? []),
    [data?.roadIds, supportRoadId]
  );
  const routeRoadRanges = useRoadRangesById(closingRoadIds);
  const routeRoadNames = useRoadNamesById(closingRoadIds);
  const pkEndTyped = watch('pkEnd');
  // Prevención P2: la casilla «Último tramo del turno» no se ofrece con causa
  // «Actuación» (ver el render).
  const pauseCauseValue = watch('pauseCause');
  // QA T6 (banco, 15-oct): la casilla solo tiene sentido con una causa ya
  // elegida —es la causa la que decide si se admite—, y marcada cambia lo que
  // hace «Guardar»: también entrega el recorrido.
  const lastSegmentAllowed =
    !!data?.offerLastSegment && !!pauseCauseValue && pauseCauseValue !== 'action';
  const deliversRoute = lastSegmentAllowed && isLastSegment;

  // Por qué carreteras pasó el tramo que se cierra. El cálculo vive en
  // `useClosingRoadStretches` y NO aquí desde 2026-09-14: estaba copiado dentro
  // de este modal y el botón «Canvi de sentit», que cierra un tramo igual, se
  // quedó sin él year y medio — el tramo que cerraba un giro dentro del ramal
  // se guardaba como la troncal entera. Compartido, un camino nuevo lo hereda.
  const roadStretches = useClosingRoadStretches({
    routeId: data?.routeId,
    segmentRoadId: data?.roadId,
    roadIds: closingRoadIds,
    segmentStartDate: data?.segmentStartDate,
    segmentPkInit: data?.segmentPkInit,
    pkEnd: parsePkString(pkEndTyped ?? ''),
  });

  /**
   * «Pasaste a la C-25z en el PK 234+400.» — el aviso de que el tramo se ha
   * partido solo. Sale del MISMO `roadStretches` que se va a enviar, así que
   * lo que lee el operario es exactamente lo que se guarda; derivarlo por otro
   * camino sería otra fuente de verdad que puede discrepar.
   */
  const crossingNotice = useMemo(() => {
    if (!roadStretches || roadStretches.length < 2) return null;
    const lastStretch = roadStretches[roadStretches.length - 1];
    const roadName = routeRoadNames[lastStretch.roadId];
    if (!roadName) return null;
    return t('surveillance.pauseModal.roadCrossingSplit', {
      road: roadName,
      pk: `${lastStretch.pkInitKm}+${String(lastStretch.pkInitMeter).padStart(3, '0')}`,
    });
  }, [roadStretches, routeRoadNames, t]);

  /**
   * «Acabas en la C-16, que no es de este recorrido» — solo cuando los trozos
   * que se van a ENVIAR acaban en ella, que es cuando el servidor la añadirá.
   */
  const supportRoadNotice = useMemo(() => {
    const endRoadId = roadStretches?.[roadStretches.length - 1]?.roadId;
    if (!endRoadId || endRoadId !== supportRoadId) return null;
    return t('surveillance.pauseModal.outsideRouteRoad', {
      road: routeRoadNames[endRoadId] || endRoadId,
    });
  }, [roadStretches, supportRoadId, routeRoadNames, t]);

  const resolveGpsPk = useGpsToPK();
  /**
   * Ya se ha rellenado (o se está rellenando) — el efecto no puede repetirse.
   *
   * Va con `useRef` y no en las deps porque el efecto pide la posición al GPS:
   * re-ejecutarlo dispararía una lectura más por cada render.
   */
  const prefillDoneRef = useRef(false);
  /**
   * La última posición leída para el PK (spec aviso por zona § 6): el sensor
   * gana al rastro, igual que con el PK. Se manda con el cierre.
   */
  const lastFixRef = useRef<{
    lat: number;
    lng: number;
    source: 'sensor' | 'track';
  } | null>(null);
  /**
   * 🔴 La cancelación y el reloj viven en refs, NO dentro del efecto.
   *
   * El efecto depende de `data?.roadIds`, que es un ARRAY: llega una
   * referencia nueva en cada render, así que el efecto se re-ejecuta muchas
   * veces aunque el contenido no cambie. Y React ejecuta la LIMPIEZA antes de
   * cada re-ejecución: con `cancelled` y el `setTimeout` dentro, cada render
   * mataba la espera al sensor y borraba el reloj de rescate... que entonces
   * ya no se volvía a armar, porque el guard `prefillHechoRef` corta arriba.
   *
   * Resultado: bastaba UN render durante los 5,5 s para que el prellenado no
   * ocurriera nunca. Y el modal re-renderiza solo — al calcular los trozos, al
   * llegar los rangos de las carreteras, al teclear en cualquier campo.
   *
   * Ahora solo se cancela al DESMONTAR, que es lo único que de verdad invalida
   * el trabajo en curso.
   */
  const cancelledRef = useRef(false);
  useEffect(() => {
    // 🔴 El CUERPO importa tanto como la limpieza (2026-08-27).
    //
    // `React.StrictMode` monta, desmonta y vuelve a montar en desarrollo. Con
    // un efecto que solo devolvía la limpieza —`() => () => {…}`, sin cuerpo—,
    // ese desmontaje simulado dejaba `cancelado = true` y NADIE lo devolvía a
    // false: el prellenado nacía cancelado y el campo no se rellenaba jamás.
    // Cazado con el rastro `[pk-prefill]` puesto para eso: «SALE: cancelado o
    // sin routeId {cancelado: true, routeId: '6a8f…'}» — el id estaba bien, lo
    // que sobraba era la cancelación.
    //
    // `prefillHechoRef` se reinicia por lo mismo: tras el remontaje, el efecto
    // del prellenado salía por «ya se intentó antes» y el reloj de rescate,
    // que la limpieza acababa de borrar, no se volvía a armar nunca.
    cancelledRef.current = false;
    prefillDoneRef.current = false;
    return () => {
      cancelledRef.current = true;
    };
  }, []);
  useEffect(() => {
    const roadId = data?.roadId;
    trace('efecto', {
      roadId,
      roadIds: data?.roadIds,
      routeId: data?.routeId,
      segmentStartDate: data?.segmentStartDate,
      hasSensor: !!navigator.geolocation,
      online: navigator.onLine,
    });
    if (!roadId || !navigator.geolocation) {
      trace('SALE: sin roadId o sin sensor');
      return;
    }
    // 🔴 Reintentar cuando llegue el payload, no rendirse en el primer render.
    //
    // Antes las deps eran `[]`: si el modal montaba con `data` todavía sin
    // `roadId`, el efecto salía por el guard de arriba y NO volvía nunca —
    // campo vacío y a teclear el PK a mano teniendo el aparato la posición
    // delante. Sin cobertura eso es peor que un incordio: el operario está en
    // la carretera sin nada que le diga dónde está.
    if (prefillDoneRef.current) {
      trace('SALE: ya se intento antes');
      return;
    }
    prefillDoneRef.current = true;

    /**
     * El sensor gana al rastro cuando contesta: su lectura es de AHORA.
     * Mientras tanto el campo no se queda vacío, que es lo que importa.
     */
    let sensorWins = false;

    /**
     * Rellena el campo desde unas coordenadas, sean del sensor o del rastro.
     * Dice qué pasó para que el rastro sepa si probar con el punto anterior:
     * `'no-pk'` es «este no cae en ninguna vía».
     */
    const fillFrom = async (
      lat: number,
      lng: number,
      source: 'sensor' | 'track'
    ): Promise<WalkBackOutcome> => {
      // Se guarda antes de cualquier salida: aunque el operario ya haya
      // tecleado el PK, la posición sigue siendo dónde paró.
      if (source === 'sensor' || lastFixRef.current?.source !== 'sensor') {
        lastFixRef.current = { lat, lng, source };
      }
      if (cancelledRef.current) return 'stop';
        // 🔴 Se busca en TODAS las carreteras del recorrido, no solo en la del
        // tramo (2026-08-17).
        //
        // Buscar solo en la del tramo rompía justo el caso que motiva la
        // feature: la patrulla se pasa de la troncal al ramal, para, y al
        // cerrar está a más de 300 m de la vía donde abrió el tramo → no
        // resolvía nada → campo vacío y a teclear el PK a mano, teniendo el
        // aparato la posición delante. Con todas, el PK sale solo y el resto
        // —encontrar el cruce en el rastro y partir el tramo— ya está montado.
        //
        // La preferencia sigue siendo la carretera del tramo: en el asfalto
        // que la troncal y el ramal comparten los candidatos están a metros, y
        // ahí lo correcto es no moverse de donde se venía.
      trace('resolviendo PK desde', { lat, lng });
      const pk = await resolveGpsPk({
        lat,
        lng,
        roadIds: data?.roadIds?.length ? data.roadIds : [roadId],
        preferredRoadId: roadId,
        // Solo si NINGUNA del recorrido explica el punto (2026-09-23).
        otherRoadIds: supportCandidatesRef.current,
      });
      trace('PK resuelto', { source, ...pk });
      if (source === 'track' && sensorWins) {
        trace('SALE: el sensor ya habia contestado, el rastro no pisa');
        return 'stop';
      }
      if (cancelledRef.current || !pk) {
        trace('SALE: cancelado o sin PK', { cancelled: cancelledRef.current, pk });
        return cancelledRef.current ? 'stop' : 'no-pk';
      }
      // Aunque el operario ya haya tecleado: DÓNDE está no depende de eso, y
      // es lo que decide contra qué vía se calculan los trozos.
      if (pk.outsideRoute) setSupportRoadId(pk.roadId);
      // No pisar lo que el operario ya haya tecleado mientras el GPS→PK
      // resolvía (2026-07-17 — visible sobre todo con red lenta).
      if (getFieldState('pkEnd').isDirty) {
        trace('SALE: el operario ya escribio en el campo');
        return 'done';
      }
      const pkValue = `${pk.pkKm}+${pk.pkMeter.toString().padStart(3, '0')}`;
      trace('RELLENANDO el campo con', { pkValue, source });
      setValue('pkEnd', pkValue, { shouldValidate: false, shouldDirty: false });
      return 'done';
    };

    /**
     * 🔴 Si el sensor no contesta, se usa el ÚLTIMO punto del rastro.
     *
     * `getCurrentPosition` puede no llamar a NINGUNO de sus dos callbacks: se
     * comprobó en vivo que, con la red cortada, Chrome no entrega la posición
     * ni con el override de DevTools puesto. En una tablet real el GPS no
     * necesita red, pero tampoco es infalible — una lectura puede tardar más
     * que el `timeout` con el equipo bajo un puente o arrancando en frío.
     *
     * Y no hace falta rendirse: la app **lleva todo el recorrido grabando la
     * posición**. Cerrar un tramo con el campo vacío teniendo el rastro en el
     * dispositivo es tirar un dato que ya está ahí. Se usa el último punto,
     * que es de hace segundos.
     *
     * El sensor manda si contesta; el rastro es la red de seguridad.
     */
    const fillFromTrack = async () => {
      trace('tirando del rastro');
      if (cancelledRef.current || !data?.routeId) {
        trace('SALE: cancelado o sin routeId', {
          cancelled: cancelledRef.current,
          routeId: data?.routeId,
        });
        return;
      }
      try {
        const allPoints = await getLocalTrack(data.routeId);
        const points = pointsWithin(allPoints, data.segmentStartDate);
        trace('rastro leido', {
          totalInRoute: allPoints.length,
          insideSegment: points.length,
          since: data.segmentStartDate,
        });
        if (points.length === 0) {
          trace('SALE: el rastro no tiene ningun punto dentro del tramo');
          return;
        }
        // 🔴 El último punto que CAIGA EN UNA VÍA, no el último a secas
        // (2026-09-29). Una lectura mala suelta al final —en QA-3, la
        // ubicación real que Chrome coló al recargar; en carretera, la salida
        // de un túnel— dejaba el PK vacío y, sin reconocer el ramal, el PK
        // tecleado a mano se rechazaba teniendo seis puntos buenos detrás.
        await walkBackTrack(points, (point) =>
          fillFrom(point.latitude, point.longitude, 'track')
        );
      } catch (err) {
        trace('SALE: el rastro no se pudo leer', err);
      }
    };

    // 🔴 El rastro PRIMERO, sin esperar a nadie (2026-08-27, tras probarlo).
    //
    // Antes se esperaba al sensor y solo entonces se tiraba del rastro: entre
    // el `timeout` de la API y el reloj de rescate, el operario veía el campo
    // vacío SEIS SEGUNDOS para un dato que el aparato ya tenía guardado. Y en
    // el caso que motiva todo esto —sin cobertura— el sensor acaba fallando,
    // así que esa espera no compraba nada.
    //
    // El rastro es local: se lee al instante. Y su último punto es de hace
    // segundos, porque la app graba mientras el tramo está abierto: parada, la
    // posición no ha cambiado; rodando, hay un punto cada 10-15 m.
    //
    // El sensor se sigue pidiendo en paralelo y GANA si contesta, que su
    // lectura es de ahora. Lo que ya no hace es tener el campo vacío mientras.
    void fillFromTrack();

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        sensorWins = true;
        trace('el sensor CONTESTA', {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          acc: pos.coords.accuracy,
        });
        void fillFrom(pos.coords.latitude, pos.coords.longitude, 'sensor');
      },
      (err) => {
        trace('el sensor da ERROR (el rastro ya ha respondido)', {
          code: err.code,
          msg: err.message,
        });
      },
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 2000 }
    );
    // Sin `return`: la limpieza vive en el efecto de desmontaje de arriba. Un
    // re-render NO puede cancelar un prellenado en vuelo.
  }, [data?.roadId, data?.roadIds, resolveGpsPk, getFieldState, setValue]);

  // V4 D7: 3 causas (basic | action | external-call). Labels via i18n `pages:surveillance.pause.causes.*`.
  const causeOptions = [
    { value: 'basic', label: t('surveillance.pause.causes.basic') },
    { value: 'action', label: t('surveillance.pause.causes.action') },
    {
      value: 'external-call',
      label: t('surveillance.pause.causes.external-call'),
    },
  ];


  /**
   * Siembra los extremos de la carretera en el formulario para que el
   * `.test('pk-validation')` del schema tenga contra qué comparar.
   *
   * Va por `setValue` y no por `defaultValues` porque `useRoadPkRange` resuelve
   * ASÍNCRONO: si el recorrido se abre en frío, la carretera llega después de
   * montar el modal. Mismo gesto que hace `NewRoute` al elegir carretera.
   */
  useEffect(() => {
    // Cuando el tramo cruzó de carretera, el PK final es de la de DESTINO: es
    // ahí donde se cierra el tramo nuevo, así que validar contra ella es lo
    // correcto. Sin esto el campo seguiría en rojo por comparar con la vía de
    // la que la patrulla ya se había ido.
    const endRoadId = roadStretches?.[roadStretches.length - 1]?.roadId;
    if (endRoadId) {
      const bounds = roadPkRangeAFormulario(routeRoadRanges[endRoadId]);
      setValue('pkInitRoad', bounds.pkInitRoad);
      setValue('pkEndRoad', bounds.pkEndRoad);
      return;
    }

    // 🔴 Sin trozos, se valida contra la vía del recorrido donde el PK QUEPA
    // (2026-08-26).
    //
    // El front dejó de calcular bifurcaciones: con recorrido de varias vías y
    // sin rastro, `resolveClosingStretches` devuelve `null` y antes eso hacía
    // caer la validación al rango de la vía de ORIGEN — el campo en rojo y el
    // operario sin poder cerrar en el ramal. Justo el caso que el servidor
    // acaba de desbloquear deduciendo el cruce.
    //
    // No se usa la unión de todos los rangos, que sería más simple: aceptaría
    // un PK que no cabe en NINGUNA vía —el hueco entre dos rangos disjuntos— y
    // el servidor lo rechazaría al sincronizar… y la cola offline descarta lo
    // rechazado. Se busca la vía concreta que lo explica, o se deja el rango de
    // siempre para que el error salga aquí y no se pierda nada.
    const pk = parsePkString(pkEndTyped ?? '');
    const explainingRoadId =
      pk &&
      (data?.roadIds ?? []).find((id) =>
        pkWithinRange(pk, routeRoadRanges[id])
      );
    const bounds = roadPkRangeAFormulario(
      explainingRoadId ? routeRoadRanges[explainingRoadId] : roadPkRange
    );
    setValue('pkInitRoad', bounds.pkInitRoad);
    setValue('pkEndRoad', bounds.pkEndRoad);
  }, [
    roadPkRange,
    routeRoadRanges,
    roadStretches,
    pkEndTyped,
    data?.roadIds,
    setValue,
  ]);

  const handleClose = useCallback(() => {
    reset();
    onClickClose();
  }, [reset, onClickClose]);

  const onSubmit = useCallback(
    async (formData: PauseTimerFormData) => {
      if (!data?.onConfirm || isSubmitting) return;
      const pk = parsePkString(formData.pkEnd);
      if (!pk) return;
      setIsSubmitting(true);
      try {
        await data.onConfirm({
          pkEndKm: pk.km,
          pkEndMeter: pk.meter,
          // Si pasó por varias vías, el tramo las registra. No se parte.
          roadStretches: roadStretches ?? undefined,
          pauseCause: formData.pauseCause,
          pauseCauseDetail: formData.pauseCauseDetail,
          lane: formData.lane,
          endLatitude: lastFixRef.current?.lat,
          endLongitude: lastFixRef.current?.lng,
          // Con causa «Actuación» la casilla no se ofrece (ver abajo): aunque
          // quedara marcada de antes, no viaja.
          isLastSegment:
            !!data.offerLastSegment &&
            isLastSegment &&
            formData.pauseCause !== 'action',
        });
        // Fix 2026-07-03: cerrar el modal tras confirmar. Antes solo se hacía
        // reset() y el modal quedaba montado TAPANDO la página siguiente (con
        // causa 'action' la app navega a new-action?fromSegment por debajo).
        handleClose();
      } finally {
        setIsSubmitting(false);
      }
    },
    [data, handleClose, isSubmitting, roadStretches, isLastSegment]
  );

  return (
    <ModalContainerStyled
      // width 60 (el de EditTeamModal): con 50 el textarea quedaba estrecho.
      // El título va DENTRO (centrado, con icono) como ChangeStateModal/
      // ChangePauseCause — mismo título "Causa de la parada", así que
      // comparten composición. Aquí no se pasa `title`.
      width={60}
      onClickClose={handleClose}
      fill={theme.colors.black}
    >
      {isSubmitting && (
        <ContainerLoader>
          <Loader />
        </ContainerLoader>
      )}
      <ContainerForm onSubmit={handleSubmit(onSubmit)} isLoading={isSubmitting}>
        <AlertIcon />

        <ContainerText>
          <Title variant="semiBold">{t('surveillance.pauseModal.title')}</Title>
          <Text size="M" colorText="greyDark">
            {t('surveillance.pauseModal.subtitle')}
          </Text>
        </ContainerText>

        {/* PK Final */}
        <Section>
          <Text size="S">{t('surveillance.pkEnd')}</Text>
          <PkFieldWrap>
            <InputText
              {...registerPk(register, setValue, 'pkEnd')}
              typeInput="normal"
              placeholder={t('common:pkExample')}
              inputSize="S"
              borderError={!!errors.pkEnd}
              error={errors.pkEnd?.message ? t(errors.pkEnd.message) : undefined}
            />
          </PkFieldWrap>
          {/* El tramo cruzó de carretera: decirlo. Cerrar en el ramal con un PK
              que no es de «su» carretera desconcierta al operario, que no sabe
              si ha quedado registrado. El texto ya estaba traducido en los tres
              idiomas desde que se automatizó el corte; solo faltaba pintarlo. */}
          {crossingNotice && (
            <Text size="S" colorText="greyDark">
              {crossingNotice}
            </Text>
          )}
          {supportRoadNotice && (
            <Text
              size="S"
              colorText="greyDark"
              data-testid="pause-segment-support-road-notice"
            >
              {supportRoadNotice}
            </Text>
          )}
        </Section>

        {/* Cause */}
        <Section>
          <Text size="S">{t('surveillance.pause.cause')}</Text>
          <Controller
            name="pauseCause"
            control={control}
            render={({ field }) => (
              <SearchableDropdown
                inputSize="L"
                typeDropDown="Default"
                placeholder={t('surveillance.pauseModal.selectCause')}
                options={causeOptions}
                onChangeSelected={(option) =>
                  field.onChange(option?.value || '')
                }
                selectedOption={field.value}
                borderError={!!errors.pauseCause}
                error={errors.pauseCause?.message}
              />
            )}
          />
        </Section>

        {/* Carril — D1 (2026-07-23): el carril se registra al CERRAR el tramo.
            Pre-relleno editable con el carril actual del tramo (defaultLane). */}
        <Section>
          <Text size="S">{t('forms:workOrder.lane')}</Text>
          <Controller
            name="lane"
            control={control}
            render={({ field: { onChange, value } }) => (
              <DropDownInput
                id="pause-lane-select"
                inputSize="L"
                typeDropDown="Default"
                placeholder={t('common:labels.select')}
                options={getLaneOptions(t)}
                selectedOption={value}
                onChangeSelected={(option) => onChange(option?.value ?? '')}
                borderError={!!errors.lane}
                error={errors.lane?.message}
              />
            )}
          />
        </Section>

        {/* Detail (optional) */}
        <Section>
          <Text size="S">{t('surveillance.pauseModal.detail')}</Text>
          <TextAreaWrap>
            <Controller
              name="pauseCauseDetail"
              control={control}
              render={({ field }) => (
                <InputTextArea
                  value={field.value || ''}
                  onChange={(e) => field.onChange(e.target.value)}
                  placeholder={t('surveillance.pauseModal.detailPlaceholder')}
                  disabled={false}
                />
              )}
            />
          </TextAreaWrap>
        </Section>

        {/* Prevención P2 (plan 2026-10-15 § 5): Su.3827 y Su.3898 nacieron de
            una noche que cerró «Fin de servicio» como PAUSA y no mandó a
            validar; el turno siguiente siguió en ese recorrido. Marcada, tras
            cerrar el tramo el cronómetro finaliza el recorrido con el mismo
            flujo que el botón «Finalizar».
            🔴 No con causa «Actuación»: esa cadena crea la básica justo
            después, y un recorrido ya entregado no la admite — sin red, la cola
            la descartaría al sincronizar. */}
        {lastSegmentAllowed && (
          <Section>
            <OptionRow>
              <CheckBox
                indeterminate={false}
                checked={isLastSegment}
                onChange={(e) => setIsLastSegment(e.target.checked)}
                data-testid="pause-last-segment-of-shift"
              />
              <Text size="S" component="span">
                {t('surveillance.pauseModal.lastSegmentOfShift')}
              </Text>
            </OptionRow>
            {isLastSegment && (
              <Text size="S" colorText="greyDark">
                {t('surveillance.pauseModal.lastSegmentOfShiftHint')}
              </Text>
            )}
          </Section>
        )}

        <ButtonRow>
          <ActionButton
            type="button"
            text={t('common:buttons.cancel')}
            variant="secondary"
            onClick={handleClose}
            disabled={isLoading || isSubmitting}
          />
          <ActionButton
            type="submit"
            // Marcada «Último tramo del turno», guardar también ENTREGA el
            // recorrido: el botón no puede seguir diciendo «Guardar pausa».
            text={t(
              deliversRoute
                ? 'surveillance.pauseModal.saveAndDeliver'
                : 'surveillance.pauseModal.save'
            )}
            disabled={isLoading || isSubmitting}
          />
        </ButtonRow>
      </ContainerForm>
    </ModalContainerStyled>
  );
};

// Backwards-compat default + alias so existing SuperModal switch still finds it
export default PauseTimerModal;
export { PauseTimerModal as PauseSurveillanceRouteModal };
